import React, { useRef, useState, useEffect } from 'react';
import styles from './GiveawayModal.module.scss';
import { useModalStore } from '../../../store/ModalStore';
import Modal from '@/components/Modal/Modal';
import WebApp from '@twa-dev/sdk';

import useBalanceStore from '@/store/BalanceStore';
import { ReactComponent as HeartIcon } from '@/assets/Icons/heart.svg';
import { ReactComponent as PlusIcon } from '@/assets/Icons/plus-circle.svg';
import { ReactComponent as MinusIcon } from '@/assets/Icons/minus-circle.svg';
import { ReactComponent as CouponLogo } from '@/assets/Icons/coupon.svg';

interface GiveawayModalProps {
  couponType: string;
  couponPrice: number;
  onPurchase: (giveawayId: number, amount: number, couponType: string) => void;
  purchased?: number;
  giveawayId: number;
}

export default function GiveawayModal({
  couponType,
  couponPrice,
  onPurchase,
  purchased = 0,
  giveawayId,
}: GiveawayModalProps) {
  const [couponAmount, setCouponAmount] = useState<number>(1);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentAmountRef = useRef<number>(1);

  const handleClose = useModalStore((state) => state.handleClose);
  const balanceHearts = useBalanceStore((state) => state.balance);

  const count = currentAmountRef.current * couponPrice;
  const balance = couponType === 'hearts' ? balanceHearts.hearts : Infinity;
  const maxCoupons = 5;
  const remainingCoupons = maxCoupons - purchased;
  const tg = window.Telegram?.WebApp;
  // Инициализация кнопки
  useEffect(() => {
    tg?.MainButton.setParams({
      text_color: '#000000',
      color: '#FFFFFF',
    });
    tg?.MainButton.enable();
    tg?.MainButton.show();
    tg?.MainButton.onClick(handleSubmit);
    return () => {
      tg?.MainButton.offClick(handleSubmit);
      tg?.MainButton.hide();
    };
  }, []);
  // Обновление текста и состояния кнопки
  useEffect(() => {
    const canPurchase =
      couponType === 'hearts'
        ? count <= balance && currentAmountRef.current <= remainingCoupons
        : true;

    tg?.MainButton.setText(`Участвовать за ${count} ❤️`);

    if (!canPurchase) {
      tg?.MainButton.disable();
    } else {
      tg?.MainButton.enable();
    }
  }, [count, balance, remainingCoupons, couponType]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);

    if (isNaN(value) || value <= 0) {
      setCouponAmount(1);
      currentAmountRef.current = 1;
      return;
    }

    if (couponType === 'hearts') {
      const maxAmount = Math.min(
        Math.floor(balance / couponPrice),
        remainingCoupons,
      );
      const newAmount = Math.min(value, maxAmount);
      setCouponAmount(newAmount);
      currentAmountRef.current = newAmount;
    } else {
      setCouponAmount(value);
      currentAmountRef.current = value;
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      isFocused &&
      e.key >= '0' &&
      e.key <= '9' &&
      inputRef.current?.value.length === 1
    ) {
      e.preventDefault();
      const newValue = parseInt(e.key, 10);
      if (couponType === 'hearts') {
        const maxAmount = Math.min(
          Math.floor(balance / couponPrice),
          remainingCoupons,
        );
        setCouponAmount(Math.min(newValue, maxAmount));
        currentAmountRef.current = Math.min(newValue, maxAmount);
      } else {
        setCouponAmount(newValue);
        currentAmountRef.current = newValue;
      }
    }
  };

  const handleQuickSelect = (amount: number) => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (couponType === 'hearts') {
      const maxAmount = Math.min(
        Math.floor(balance / couponPrice),
        remainingCoupons,
      );
      const newAmount = Math.min(amount, maxAmount);
      setCouponAmount(newAmount);
      currentAmountRef.current = newAmount;
    } else {
      setCouponAmount(amount);
      currentAmountRef.current = amount;
    }
  };

  const handleIncrease = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (couponType === 'hearts') {
      const maxAmount = Math.min(
        Math.floor(balance / couponPrice),
        remainingCoupons,
      );
      if (couponAmount < maxAmount) {
        const newAmount = couponAmount + 1;
        setCouponAmount(newAmount);
        currentAmountRef.current = newAmount;
      }
    } else {
      const newAmount = couponAmount + 1;
      setCouponAmount(newAmount);
      currentAmountRef.current = newAmount;
    }
  };

  const handleDecrease = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (couponAmount > 1) {
      const newAmount = couponAmount - 1;
      setCouponAmount(newAmount);
      currentAmountRef.current = newAmount;
    }
  };

  const handleSubmit = () => {
    const canPurchase =
      couponType === 'hearts'
        ? count <= balance && currentAmountRef.current <= remainingCoupons
        : true;

    if (canPurchase) {
      onPurchase(giveawayId, currentAmountRef.current, couponType);
      WebApp.MainButton.hide();
      handleClose();
    }
  };

  // Добавляем обработчик для принудительного фокуса при тапе
  const handleContainerClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      setIsFocused(true);
    }
  };

  return (
    <Modal>
      <div className={styles.modalContainer} ref={modalRef}>
        <h2 className={styles.modalTitle}>Введи количество билетов</h2>
        <p className={styles.modalSubtitle}>1 билет = 1 шанс выигрыша</p>

        {couponType === 'hearts' && (
          <div className={styles.balanceInfo}>
            Твой баланс: {balance} <HeartIcon />
          </div>
        )}

        <div
          className={
            styles.couponSelector +
            ' ' +
            (couponType === 'hearts'
              ? styles.couponSelectorDC
              : styles.couponSelectorStar)
          }
        >
          <button
            className={styles.counterButton}
            onClick={handleDecrease}
            disabled={couponAmount <= 1}
          >
            <MinusIcon />
          </button>

          <div className={styles.inputWrapper}>
            <div className={styles.fakeInput}>
              {currentAmountRef.current}
              <CouponLogo />
            </div>
            <input
              onClick={handleContainerClick}
              ref={inputRef}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className={styles.hiddenInput}
              value={currentAmountRef.current}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              maxLength={4}
            />
          </div>

          <button
            className={styles.counterButton}
            onClick={handleIncrease}
            disabled={
              couponType === 'hearts' &&
              currentAmountRef.current >=
                Math.min(Math.floor(balance / couponPrice), remainingCoupons)
            }
          >
            <PlusIcon />
          </button>
        </div>

        {balance < couponPrice && (
          <div className={styles.remainingInfo}>
            Недостаточное количество Сердечек на балансе
          </div>
        )}

        <div className={styles.quickSelect}>
          <button
            className={`${styles.quickButton} ${
              currentAmountRef.current === 1 ? styles.quickButtonActive : ''
            }`}
            onClick={() => handleQuickSelect(1)}
            disabled={remainingCoupons < 1 || balance < couponPrice}
          >
            <span>1</span>
            <CouponLogo />
          </button>

          <button
            className={`${styles.quickButton} ${
              currentAmountRef.current === 3 ? styles.quickButtonActive : ''
            }`}
            onClick={() => handleQuickSelect(3)}
            disabled={remainingCoupons < 3 || balance < couponPrice * 3}
          >
            <span>3</span>
            <CouponLogo />
          </button>

          <button
            className={`${styles.quickButton} ${
              currentAmountRef.current === 5 ? styles.quickButtonActive : ''
            }`}
            onClick={() => handleQuickSelect(5)}
            disabled={remainingCoupons < 5 || balance < couponPrice * 5}
          >
            <span>5</span>
            <CouponLogo />
          </button>
        </div>
      </div>
    </Modal>
  );
}
